// ============================================================
// VidyaTrack — Data Context
// Firebase Firestore (primary) + Realtime Database (live feed)
// No mock / demo data
// ============================================================

import React, { createContext, useContext, useCallback } from "react";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  getDocs, query, where, orderBy, serverTimestamp, getDoc, limit,
} from "firebase/firestore";
import {
  ref, push, set, onValue, off,
  serverTimestamp as rtTimestamp,
} from "firebase/database";
import { db, rtdb } from "../firebase/config";
import { sanitiseDoc } from "../utils/helpers";

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

// ── Firestore helpers ────────────────────────────────────────
const fsGet = async (col, constraints = []) => {
  const q = constraints.length
    ? query(collection(db, col), ...constraints)
    : query(collection(db, col), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => sanitiseDoc({ id: d.id, ...d.data() }));
};

const fsAdd = async (col, data) => {
  const ref = await addDoc(collection(db, col), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ref.id, ...data };
};

const fsUpdate = async (col, id, data) =>
  updateDoc(doc(db, col, id), { ...data, updatedAt: serverTimestamp() });

const fsDelete = async (col, id) => deleteDoc(doc(db, col, id));

// ── Realtime DB helper ───────────────────────────────────────
const rtPush = async (path, data) =>
  push(ref(rtdb, path), { ...data, ts: rtTimestamp() }).catch(() => null);

// ============================================================
export const DataProvider = ({ children }) => {

  // ── USERS ─────────────────────────────────────────────────
  const getUser = useCallback(async (uid) => {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }, []);

  const getStudents = useCallback(async (classId) => {
    const constraints = [where("role", "==", "student")];
    if (classId) constraints.push(where("classId", "==", classId));
    return fsGet("users", constraints);
  }, []);

  const getTeachers = useCallback(async () =>
    fsGet("users", [where("role", "==", "teacher")]), []);

  const getParents = useCallback(async () =>
    fsGet("users", [where("role", "==", "parent")]), []);

  const updateUser = useCallback(async (uid, data) =>
    fsUpdate("users", uid, data), []);

  const deleteUser = useCallback(async (uid) =>
    fsDelete("users", uid), []);

  // ── CLASSES ───────────────────────────────────────────────
  const getClasses = useCallback(async () =>
    fsGet("classes"), []);

  const addClass = useCallback(async (data) =>
    fsAdd("classes", data), []);

  const updateClass = useCallback(async (id, data) =>
    fsUpdate("classes", id, data), []);

  const deleteClass = useCallback(async (id) =>
    fsDelete("classes", id), []);

  // ── HOMEWORK ──────────────────────────────────────────────
  const getHomework = useCallback(async (filters = {}) => {
    const constraints = [orderBy("createdAt", "desc")];
    if (filters.classId)   constraints.unshift(where("classId",   "==", filters.classId));
    if (filters.teacherId) constraints.unshift(where("teacherId", "==", filters.teacherId));
    return fsGet("homework", constraints);
  }, []);

  const addHomework = useCallback(async (data) => {
    const hw = await fsAdd("homework", data);
    // Notify via Realtime DB
    rtPush(`notifications/class_${data.classId}`, {
      type   : "homework",
      title  : `New Homework: ${data.title}`,
      subject: data.subject,
      hwId   : hw.id,
    });
    return hw;
  }, []);

  const updateHomework = useCallback(async (id, data) =>
    fsUpdate("homework", id, data), []);

  const deleteHomework = useCallback(async (id) =>
    fsDelete("homework", id), []);

  // ── SUBMISSIONS ───────────────────────────────────────────
  const getSubmissions = useCallback(async (filters = {}) => {
    const constraints = [];
    if (filters.homeworkId) constraints.push(where("homeworkId", "==", filters.homeworkId));
    if (filters.studentId)  constraints.push(where("studentId",  "==", filters.studentId));
    if (!constraints.length) constraints.push(orderBy("createdAt", "desc"));
    return fsGet("submissions", constraints);
  }, []);

  const addSubmission = useCallback(async (data) => {
    // Check for duplicate
    const existing = await getDocs(
      query(collection(db, "submissions"),
        where("homeworkId", "==", data.homeworkId),
        where("studentId",  "==", data.studentId),
        limit(1)
      )
    );
    if (!existing.empty) throw new Error("You have already submitted this assignment.");

    const sub = await fsAdd("submissions", { ...data, status: "submitted" });

    // Increment count on homework document
    const hwRef = doc(db, "homework", data.homeworkId);
    const hwSnap = await getDoc(hwRef);
    if (hwSnap.exists()) {
      const cur = hwSnap.data().submissionsCount || 0;
      await updateDoc(hwRef, { submissionsCount: cur + 1 });
    }

    // Notify teacher via Realtime DB
    rtPush(`notifications/teacher_${data.teacherId || ""}`, {
      type   : "submission",
      title  : `${data.studentName} submitted ${data.homeworkTitle || "homework"}`,
      subId  : sub.id,
    });

    return sub;
  }, []);

  const updateSubmission = useCallback(async (id, data) =>
    fsUpdate("submissions", id, data), []);

  // ── ATTENDANCE ────────────────────────────────────────────
  const getAttendance = useCallback(async (filters = {}) => {
    const constraints = [];
    if (filters.studentId) constraints.push(where("studentId", "==", filters.studentId));
    if (filters.classId)   constraints.push(where("classId",   "==", filters.classId));
    if (filters.date)      constraints.push(where("date",      "==", filters.date));
    if (!constraints.length) constraints.push(orderBy("date", "desc"));
    return fsGet("attendance", constraints);
  }, []);

  const markAttendance = useCallback(async (records) => {
    if (!records?.length) return;

    // Write each record to Firestore (upsert by date+student)
    await Promise.all(records.map(r => fsAdd("attendance", r)));

    // Push live summary to Realtime DB
    const dateKey = records[0].date.replace(/-/g, "");
    const classId = records[0].classId;
    const summary = {
      present: records.filter(r => r.status === "present").length,
      absent : records.filter(r => r.status === "absent").length,
      late   : records.filter(r => r.status === "late").length,
      total  : records.length,
    };
    set(ref(rtdb, `attendance_summary/${classId}/${dateKey}`), {
      ...summary,
      updatedAt: rtTimestamp(),
    }).catch(() => null);
  }, []);

  // ── NOTICES ───────────────────────────────────────────────
  const getNotices = useCallback(async (filters = {}) => {
    const constraints = [orderBy("createdAt", "desc")];
    if (filters.audience && filters.audience !== "all") {
      constraints.unshift(where("audience", "in", ["all", filters.audience]));
    }
    const notices = await fsGet("notices", constraints);
    // Pinned first
    return notices.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  }, []);

  const addNotice = useCallback(async (data) => {
    const notice = await fsAdd("notices", data);
    rtPush("live_notices", {
      id      : notice.id,
      title   : data.title,
      priority: data.priority,
      audience: data.audience,
    });
    return notice;
  }, []);

  const updateNotice = useCallback(async (id, data) =>
    fsUpdate("notices", id, data), []);

  const deleteNotice = useCallback(async (id) =>
    fsDelete("notices", id), []);

  // ── STUDY MATERIALS ───────────────────────────────────────
  const getMaterials = useCallback(async (filters = {}) => {
    const constraints = [orderBy("createdAt", "desc")];
    if (filters.classId) constraints.unshift(where("classId", "==", filters.classId));
    return fsGet("materials", constraints);
  }, []);

  const addMaterial = useCallback(async (data) =>
    fsAdd("materials", data), []);

  const updateMaterial = useCallback(async (id, data) =>
    fsUpdate("materials", id, data), []);

  const deleteMaterial = useCallback(async (id) =>
    fsDelete("materials", id), []);

  // ── GROUPS ────────────────────────────────────────────────
  const getGroups = useCallback(async (filters = {}) => {
    const constraints = [orderBy("createdAt", "desc")];
    if (filters.classId)   constraints.unshift(where("classId",   "==", filters.classId));
    if (filters.teacherId) constraints.unshift(where("teacherId", "==", filters.teacherId));
    return fsGet("groups", constraints);
  }, []);

  const addGroup = useCallback(async (data) =>
    fsAdd("groups", data), []);

  const updateGroup = useCallback(async (id, data) =>
    fsUpdate("groups", id, data), []);

  const deleteGroup = useCallback(async (id) =>
    fsDelete("groups", id), []);

  // ── CALENDAR EVENTS ───────────────────────────────────────
  const getEvents = useCallback(async () =>
    fsGet("events", [orderBy("date", "asc")]), []);

  const addEvent = useCallback(async (data) => {
    const event = await fsAdd("events", data);
    set(ref(rtdb, `events/${event.id}`), {
      ...data,
      id: event.id,
    }).catch(() => null);
    return event;
  }, []);

  const updateEvent = useCallback(async (id, data) =>
    fsUpdate("events", id, data), []);

  const deleteEvent = useCallback(async (id) =>
    fsDelete("events", id), []);

  // ── REALTIME DB SUBSCRIPTIONS ─────────────────────────────
  const subscribeNotifications = useCallback((path, callback) => {
    const r = ref(rtdb, path);
    onValue(r, snap => {
      const val = snap.val();
      if (val) callback(Object.entries(val).map(([id, v]) => ({ id, ...v })).slice(-20).reverse());
    });
    return () => off(r);
  }, []);

  const subscribeAttendanceSummary = useCallback((classId, date, callback) => {
    const dateKey = date.replace(/-/g, "");
    const r = ref(rtdb, `attendance_summary/${classId}/${dateKey}`);
    onValue(r, snap => callback(snap.val()));
    return () => off(r);
  }, []);

  return (
    <DataContext.Provider value={{
      // Users
      getUser, getStudents, getTeachers, getParents, updateUser, deleteUser,
      // Classes
      getClasses, addClass, updateClass, deleteClass,
      // Homework
      getHomework, addHomework, updateHomework, deleteHomework,
      // Submissions
      getSubmissions, addSubmission, updateSubmission,
      // Attendance
      getAttendance, markAttendance,
      // Notices
      getNotices, addNotice, updateNotice, deleteNotice,
      // Materials
      getMaterials, addMaterial, updateMaterial, deleteMaterial,
      // Groups
      getGroups, addGroup, updateGroup, deleteGroup,
      // Events
      getEvents, addEvent, updateEvent, deleteEvent,
      // Realtime
      subscribeNotifications, subscribeAttendanceSummary,
    }}>
      {children}
    </DataContext.Provider>
  );
};
